// ============================================================
// hooks/useCalculator.js
// Hook personalizado que centraliza el estado del formulario,
// validaciones y cálculos en tiempo real
// ============================================================

import { useState, useCallback, useMemo } from 'react';
import { CURRENCIES, PRINTERS_CATALOG } from '../data/constants';
import { calculateTotalCost, formatCurrency } from '../utils/calculations';

/**
 * Estado inicial del formulario.
 * Todos los campos numéricos se manejan como strings para
 * permitir inputs vacíos sin convertir "" a 0 prematuramente.
 */
const INITIAL_STATE = {
  // --- Sección 1: Impresora ---
  selectedPrinterId: 'creality_k1c',
  customPowerWatts:  '',          // Solo se usa cuando selectedPrinterId === 'custom'
  
  // --- Sección 2: Moneda y electricidad ---
  currencyCode:      'COP',
  kwPrice:           '740.25',
  // Campos para moneda personalizada (solo activos cuando currencyCode === 'OTRA')
  customCurrencyName:   '',     // Ej: "Guaraní", "Dinar", "Shekel"
  customCurrencySymbol: '',     // Ej: "₲", "د.إ", "₪"
  customCurrencyDecimals: '2',  // Decimales de la moneda personalizada

  // --- Sección 3: Tiempo de impresión ---
  printHours:        '1',
  printMinutes:      '56',        // Minutos (del ejemplo: 1h 56m del slicer)

  // --- Sección 4: Filamento ---
  filamentType:      'pla',
  filamentPricePerKg:'',
  usedGrams:         '27.66',     // Dato del slicer en imagen 3

  // --- Sección 5: Costos adicionales ---
  maintenancePerHour:  '',
  depreciationPerHour: '',
  laborCost:           '',

  // --- Sección 6: Margen de ganancia ---
  marginPercent:       '30',

  // --- Sección 7: WhatsApp ---
  clientName:        '',
  clientPhone:       '',
  figureName:        '',
  whatsappMessage:   '',          // Se genera automáticamente pero es editable
};

/**
 * Reglas de validación por campo.
 * Cada entrada devuelve un string de error o null si es válido.
 */
const VALIDATORS = {
  kwPrice: (v) => {
    const n = parseFloat(v);
    if (!v || v === '') return 'El precio por kWh es requerido';
    if (isNaN(n) || n <= 0) return 'Debe ser un número mayor a 0';
    return null;
  },
  printHours: (v) => {
    const n = parseFloat(v);
    if (!v && v !== '0') return null; // Puede ser 0 si hay minutos
    if (isNaN(n) || n < 0) return 'Valor inválido';
    return null;
  },
  printMinutes: (v) => {
    const n = parseFloat(v);
    if (!v) return null;
    if (isNaN(n) || n < 0 || n >= 60) return 'Debe ser entre 0 y 59';
    return null;
  },
  filamentPricePerKg: (v) => {
    if (!v || v === '') return 'El precio del filamento es requerido';
    const n = parseFloat(v);
    if (isNaN(n) || n <= 0) return 'Debe ser un número mayor a 0';
    return null;
  },
  usedGrams: (v) => {
    if (!v || v === '') return 'El peso usado es requerido';
    const n = parseFloat(v);
    if (isNaN(n) || n <= 0) return 'Debe ser un número mayor a 0';
    if (n > 10000) return 'Parece un valor muy alto (máx 10kg)';
    return null;
  },
  marginPercent: (v) => {
    const n = parseFloat(v);
    if (!v && v !== '0') return null;
    if (isNaN(n) || n < 0) return 'Debe ser 0 o mayor';
    if (n > 500) return 'Margen parece excesivo (máx 500%)';
    return null;
  },
  customPowerWatts: (v, state) => {
    if (state.selectedPrinterId !== 'custom') return null;
    if (!v || v === '') return 'Ingresa la potencia de tu impresora';
    const n = parseFloat(v);
    if (isNaN(n) || n <= 0) return 'Debe ser mayor a 0 Watts';
    if (n > 5000) return 'Valor muy alto (¿más de 5000W?)';
    return null;
  },
};

/**
 * Hook principal del calculador.
 * Expone el estado, manejadores de cambio, errores de validación
 * y los resultados calculados en tiempo real.
 */
export function useCalculator() {
  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [showResults, setShowResults] = useState(false);

  // ---- Obtener la impresora actualmente seleccionada ----
  const selectedPrinter = useMemo(() => {
    return PRINTERS_CATALOG.find(p => p.id === form.selectedPrinterId)
        || PRINTERS_CATALOG[0];
  }, [form.selectedPrinterId]);

  // ---- Obtener la moneda actualmente seleccionada ----
  // Si el usuario eligió 'OTRA', construimos el objeto con sus datos personalizados
  const selectedCurrency = useMemo(() => {
    if (form.currencyCode === 'OTRA') {
      return {
        code:     'OTRA',
        symbol:   form.customCurrencySymbol  || '$',
        name:     form.customCurrencyName    || 'Moneda personalizada',
        country:  'Personalizada',
        decimals: parseInt(form.customCurrencyDecimals, 10) || 2,
        flag:     '🌐',
        referenceKwh: 0,
      };
    }
    return CURRENCIES.find(c => c.code === form.currencyCode) || CURRENCIES[0];
  }, [
    form.currencyCode,
    form.customCurrencyName,
    form.customCurrencySymbol,
    form.customCurrencyDecimals,
  ]);

  // ---- Calcular la potencia efectiva (impresora seleccionada o personalizada) ----
  const effectivePowerWatts = useMemo(() => {
    if (form.selectedPrinterId === 'custom') {
      return parseFloat(form.customPowerWatts) || 0;
    }
    return selectedPrinter.powerWatts;
  }, [form.selectedPrinterId, form.customPowerWatts, selectedPrinter]);

  // ---- Convertir horas + minutos a horas decimales ----
  const totalHours = useMemo(() => {
    const h = parseFloat(form.printHours)  || 0;
    const m = parseFloat(form.printMinutes) || 0;
    return h + (m / 60);
  }, [form.printHours, form.printMinutes]);

  // ---- Cálculo principal en tiempo real ----
  const results = useMemo(() => {
    return calculateTotalCost({
      powerWatts:          effectivePowerWatts,
      hours:               totalHours,
      kwPrice:             parseFloat(form.kwPrice)             || 0,
      filamentPricePerKg:  parseFloat(form.filamentPricePerKg)  || 0,
      usedGrams:           parseFloat(form.usedGrams)           || 0,
      maintenancePerHour:  parseFloat(form.maintenancePerHour)  || 0,
      depreciationPerHour: parseFloat(form.depreciationPerHour) || 0,
      laborCost:           parseFloat(form.laborCost)           || 0,
      marginPercent:       parseFloat(form.marginPercent)       || 0,
    });
  }, [
    effectivePowerWatts,
    totalHours,
    form.kwPrice,
    form.filamentPricePerKg,
    form.usedGrams,
    form.maintenancePerHour,
    form.depreciationPerHour,
    form.laborCost,
    form.marginPercent,
  ]);

  // ---- Función helper para formatear valores en la moneda actual ----
  const fmt = useCallback((value) => {
    return formatCurrency(value, selectedCurrency.symbol, selectedCurrency.decimals);
  }, [selectedCurrency]);

  // ---- Manejador genérico de cambios en el formulario ----
  const handleChange = useCallback((field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));

    // Validar el campo en tiempo real
    const validator = VALIDATORS[field];
    if (validator) {
      const error = validator(value, { ...form, [field]: value });
      setErrors(prev => ({
        ...prev,
        [field]: error,
      }));
    }
  }, [form]);

  // ---- Cambio de impresora: auto-rellenar datos ----
  const handlePrinterChange = useCallback((printerId) => {
    handleChange('selectedPrinterId', printerId);

    // Si se selecciona una impresora predefinida, limpiar el campo custom
    if (printerId !== 'custom') {
      setForm(prev => ({ ...prev, selectedPrinterId: printerId, customPowerWatts: '' }));
    }
  }, [handleChange]);

  // ---- Cambio de moneda: auto-sugerir precio kWh de referencia ----
  const handleCurrencyChange = useCallback((code) => {
    const currency = CURRENCIES.find(c => c.code === code);
    if (currency) {
      setForm(prev => ({
        ...prev,
        currencyCode: code,
        kwPrice: String(currency.referenceKwh),
      }));
    }
  }, []);

  // ---- Validación completa antes de calcular ----
  const validateAll = useCallback(() => {
    const newErrors = {};
    const fieldsToValidate = [
      'kwPrice',
      'filamentPricePerKg',
      'usedGrams',
      'marginPercent',
      'customPowerWatts',
    ];

    fieldsToValidate.forEach(field => {
      const validator = VALIDATORS[field];
      if (validator) {
        const error = validator(form[field], form);
        if (error) newErrors[field] = error;
      }
    });

    // Validar que haya tiempo de impresión
    const h = parseFloat(form.printHours)  || 0;
    const m = parseFloat(form.printMinutes) || 0;
    if (h === 0 && m === 0) {
      newErrors.printHours = 'Ingresa al menos 1 minuto de impresión';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  // ---- Acción principal: calcular ----
  const handleCalculate = useCallback(() => {
    if (validateAll()) {
      setShowResults(true);
    }
  }, [validateAll]);

  // ---- Reset del formulario ----
  const handleReset = useCallback(() => {
    setForm(INITIAL_STATE);
    setErrors({});
    setShowResults(false);
  }, []);

  return {
    // Estado del formulario
    form,
    errors,
    showResults,

    // Datos derivados
    selectedPrinter,
    selectedCurrency,
    effectivePowerWatts,
    totalHours,
    results,

    // Acciones
    handleChange,
    handlePrinterChange,
    handleCurrencyChange,
    handleCalculate,
    handleReset,
    fmt,
  };
}
